import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, MapPin, Briefcase } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";

const ROLE_FAMILIES = ["CEO", "CTO", "CMO", "CFO", "COO", "CPO", "CRO", "Chief AI Officer", "Head of AI", "VP Engineering", "VP Product", "Head of Sales", "General Counsel"];

export default function OrgDetail() {
  const { id } = useParams();
  const [org, setOrg] = useState(null);
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    title: "", role_family: "CTO", description: "",
    remote_policy: "remote", location: "", salary_min: "", salary_max: "",
    equity_min: "", equity_max: "", experience_years: 5,
    required_skills: "", industries: "",
  });

  const load = async () => {
    try {
      const [{ data: o }, { data: r }] = await Promise.all([
        api.get(`/orgs/${id}`),
        api.get(`/roles?org_id=${id}`),
      ]);
      setOrg(o);
      setRoles(r);
    } catch { toast.error("Not found"); }
  };
  useEffect(() => { load(); }, [id]);

  const createRole = async () => {
    if (!f.title.trim() || !f.description.trim()) { toast.error("Title and description required"); return; }
    setBusy(true);
    try {
      await api.post("/roles", {
        org_id: id,
        title: f.title,
        role_family: f.role_family,
        description: f.description,
        remote_policy: f.remote_policy,
        location: f.location || null,
        salary_min: f.salary_min ? parseInt(f.salary_min) : null,
        salary_max: f.salary_max ? parseInt(f.salary_max) : null,
        equity_min: f.equity_min ? parseFloat(f.equity_min) : null,
        equity_max: f.equity_max ? parseFloat(f.equity_max) : null,
        experience_years: f.experience_years ? parseInt(f.experience_years) : null,
        required_skills: f.required_skills ? f.required_skills.split(",").map((s) => s.trim()).filter(Boolean) : [],
        industries: f.industries ? f.industries.split(",").map((s) => s.trim()).filter(Boolean) : [],
      });
      toast.success("Role posted");
      setOpen(false);
      setF({ ...f, title: "", description: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to post role");
    } finally { setBusy(false); }
  };

  if (!org) return <Skeleton className="h-64" />;

  return (
    <div className="space-y-6" data-testid="org-detail">
      <Card className="p-8">
        <div className="label-caps">{org.industry || "Organization"}</div>
        <h1 className="text-3xl md:text-4xl tracking-tighter font-semibold mt-2">{org.name}</h1>
        {org.tagline && <div className="text-lg text-muted-foreground mt-2">{org.tagline}</div>}
        <div className="mt-4 flex flex-wrap gap-3 text-xs text-muted-foreground">
          {org.stage && <span className="font-mono">Stage · {org.stage}</span>}
          {org.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{org.location}</span>}
          {org.website && <a href={org.website} target="_blank" rel="noreferrer" className="hover:text-foreground">{org.website}</a>}
        </div>
        {org.mission && <p className="mt-6 text-sm leading-relaxed max-w-3xl">{org.mission}</p>}
      </Card>

      <div className="flex items-center justify-between">
        <h2 className="text-xl font-medium">Open roles</h2>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="new-role-btn"><Plus className="h-4 w-4 mr-1" />Post a role</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Post an executive role</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Title</Label>
                  <Input value={f.title} onChange={(e) => setF({...f, title: e.target.value})} data-testid="role-title" className="mt-1.5" placeholder="Chief Technology Officer" />
                </div>
                <div>
                  <Label>Role family</Label>
                  <Select value={f.role_family} onValueChange={(v) => setF({...f, role_family: v})}>
                    <SelectTrigger data-testid="role-family" className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLE_FAMILIES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea rows={5} value={f.description} onChange={(e) => setF({...f, description: e.target.value})} data-testid="role-desc" className="mt-1.5" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Remote policy</Label>
                  <Select value={f.remote_policy} onValueChange={(v) => setF({...f, remote_policy: v})}>
                    <SelectTrigger data-testid="role-remote" className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="remote">Remote</SelectItem>
                      <SelectItem value="hybrid">Hybrid</SelectItem>
                      <SelectItem value="onsite">Onsite</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Location</Label>
                  <Input value={f.location} onChange={(e) => setF({...f, location: e.target.value})} data-testid="role-location" className="mt-1.5" />
                </div>
                <div>
                  <Label>Experience (years)</Label>
                  <Input type="number" value={f.experience_years} onChange={(e) => setF({...f, experience_years: e.target.value})} data-testid="role-exp" className="mt-1.5" />
                </div>
                <div>
                  <Label>Salary min (USD)</Label>
                  <Input type="number" value={f.salary_min} onChange={(e) => setF({...f, salary_min: e.target.value})} data-testid="role-smin" className="mt-1.5" />
                </div>
                <div>
                  <Label>Salary max (USD)</Label>
                  <Input type="number" value={f.salary_max} onChange={(e) => setF({...f, salary_max: e.target.value})} data-testid="role-smax" className="mt-1.5" />
                </div>
                <div>
                  <Label>Equity max (%)</Label>
                  <Input type="number" step="0.1" value={f.equity_max} onChange={(e) => setF({...f, equity_max: e.target.value})} data-testid="role-emax" className="mt-1.5" />
                </div>
              </div>
              <div>
                <Label>Required skills (comma separated)</Label>
                <Input value={f.required_skills} onChange={(e) => setF({...f, required_skills: e.target.value})} data-testid="role-skills" className="mt-1.5" placeholder="AI/ML, Distributed systems" />
              </div>
              <div>
                <Label>Industries (comma separated)</Label>
                <Input value={f.industries} onChange={(e) => setF({...f, industries: e.target.value})} data-testid="role-industries" className="mt-1.5" placeholder="Healthcare, SaaS" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={createRole} disabled={busy} data-testid="role-submit">{busy ? "Posting…" : "Post role"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-3">
        {roles.length === 0 ? (
          <Card className="p-10 text-center text-muted-foreground">No roles yet.</Card>
        ) : roles.map((r) => (
          <Link key={r.id} to={`/app/roles/${r.id}`} data-testid={`org-role-${r.id}`}>
            <Card className="p-5 hover:border-primary/50 transition-colors duration-200">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium">{r.title}</div>
                  <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2">
                    <Briefcase className="h-3 w-3" /> {r.role_family}
                    {r.location && <><span>·</span>{r.location}</>}
                    {r.remote_policy && <><span>·</span>{r.remote_policy}</>}
                  </div>
                </div>
                {(r.salary_min || r.salary_max) && (
                  <div className="font-mono text-xs text-muted-foreground shrink-0">
                    ${(r.salary_min || 0).toLocaleString()} - ${(r.salary_max || 0).toLocaleString()}
                  </div>
                )}
              </div>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}
